import React, { memo, useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useTheme, Button } from 'tamagui';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { CobuText, CobuButton, CobuStack } from '@cobu/ui';
import { useGroupDiscussion } from '../../../hooks/useGroupDiscussion';
import { GroupDiscussionProps, GroupComment, CommentType } from '@cobu/types';
import { Strings } from '../../../constants';
import { RootState } from '../../../store';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import CommentItem from './CommentItem';
import CommentShimmer from './CommentShimmer';
import EmptyDiscussionState from './EmptyDiscussionState';
import FloatingCommentButton from '../../ui/FloatingCommentButton';
import { EmojiProvider } from '../../../features/reactions/context/EmojiProvider';
import ReactionsModal from './ReactionsModal';
import { logger } from '../../../utils/logger';
import { showErrorMessage, showWarningMessage } from '../../../utils/messageToast';
import { useMarketplaceDetection } from '../../../hooks/useMarketplaceDetection';
import { useResidentRestrictions } from '../../../hooks/useResidentRestrictions';
// import Logger from '@/services/Logger';

const GroupDiscussion: React.FC<any> = memo(({
  groupId,
  groupEventId,
  eventDate,
  commentType = CommentType.GroupComment,
  isGroup = false,
  publicChat = false,
  buildingId,
  virtualize = true,

  // Data override (for external data management)
  comments: externalComments,
  pinnedComments: externalPinnedComments,
  groupMembers = [],

  // State override
  loading: externalLoading,
  replyLoading: externalReplyLoading,

  showLoadMoreBtnToLoadMore = false,

  // Deep linking
  targetCommentId,
  scrollToComment = false,
  onDeepLinkLoadingChange,
  skipInitialDeepLinkRefresh = false,

  // Callbacks
  onCreateComment,
  onUpdateComment,
  onDeleteComment,
  onReactToComment,
  onReportUser,
  onLoadMore,
  onRefresh,
  onComposeComment,
  onShowAllMembers,
  onUserProfile,

  // Customization
  header,
  emptyState,
  maxCommentLength = 2000,
  maxImages = 5,

  // Feature flags
  allowReactions = true,
  allowReplies = true,
  allowEditing = true,
  allowPinning = false,
  allowReporting = true,
  allowImageUploads = true,
}) => {
  const theme = useTheme();
  const strings = Strings.get().discussion;
  const { user } = useSelector((state: RootState) => state.auth);
  const chatPollingEnabled = useSelector((state: RootState) => state.settings.chatPollingEnabled);
  const estimatedItemSize = 340;
  
  // Detect if we're in marketplace context
  const { isMarketplace } = useMarketplaceDetection(groupId);
  const { canPost, canReact, canReport } = useResidentRestrictions();
  
  const composeButtonText = isMarketplace
    ? Strings.get().discussion.marketplace.composeButton 
    : strings.buttons.addComment;
  const createPostButtonText = isMarketplace
    ? Strings.get().discussion.marketplace.composeButton
    : strings.buttons.createNewPost;

  
  // Use the custom hook for data management (if external data not provided)
  const hookData = useGroupDiscussion({
    groupId: externalComments ? undefined : groupId,
    groupEventId: externalComments ? undefined : groupEventId,
    buildingId: externalComments ? undefined : buildingId,
    eventDate: externalComments ? undefined : eventDate,
    commentType,
    allowReactions,
    allowReplies,
    allowEditing,
    allowPinning,
    allowReporting,
    allowImageUploads,
    maxCommentLength,
    enableAutoRefresh: chatPollingEnabled,
    maxImages,
    onNavigateToProfile: onUserProfile,
    onNavigateToCompose: onComposeComment,
    onShowAllMembers,
  });

  // Track view events for analytics
  useEffect(() => {
    if (publicChat) {
      analyticsService.trackEvent(AnalyticsEvent.VIEW_CHAT);
    } else {
      /* ANALYTICS_DISABLED: VIEW_GROUP_DISCUSSION ('view_group_discussion')
        analyticsService.trackEvent(AnalyticsEvent.VIEW_GROUP_DISCUSSION, {
                uuid: groupId
              });
        */
    }
  }, [groupId, groupEventId, publicChat]);


  // Use external data if provided, otherwise use hook data
  const comments = externalComments || hookData.comments;
  const pinnedComments = externalPinnedComments || hookData.pinnedComments;
  const loading = externalLoading !== undefined ? externalLoading : hookData.loading;
  const refreshing = hookData.refreshing;
  const replyLoading = externalReplyLoading || hookData.replyLoading;
  const hasMoreComments = hookData.hasMoreComments;
  const isLoadingMore = hookData.isLoadingMore;

  // Action handlers (use external callbacks if provided)
  const handleDeleteComment = onDeleteComment || hookData.deleteComment;
  const handleReactToComment = onReactToComment || hookData.reactToComment;
  const handleFlagComment = hookData.flagComment;
  const handleReportUser = (userId: string) => {
    onReportUser?.(userId, 'reason');
  }

  const handleLoadMore = onLoadMore || hookData.loadMoreComments;
  const handleRefresh = onRefresh || hookData.refreshComments;

  // Local state for reactions modal
  const [reactionModalCommentId, setReactionModalCommentId] = useState<string | null>(null);
  const [isReactionModalVisible, setReactionModalVisible] = useState(false);

  // Deep linking state
  const [activeTargetCommentId, setActiveTargetCommentId] = useState<string | null>(null);
  const [highlightCommentId, setHighlightCommentId] = useState<string | null>(null);
  const [activeTargetReplyId, setActiveTargetReplyId] = useState<string | null>(null);
  const [expandedThreadCommentId, setExpandedThreadCommentId] = useState<string | null>(null);
  const [shouldPersistHighlight, setShouldPersistHighlight] = useState(false);
  const [isDeepLinkLoading, setIsDeepLinkLoading] = useState(false);
  const [pendingScrollTargetId, setPendingScrollTargetId] = useState<string | null>(null);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const consumedDeepLinkIdRef = useRef<string | null>(null);
  const refreshedForDeepLinkRef = useRef<string | null>(null);
  const [isWaitingForPreRefresh, setIsWaitingForPreRefresh] = useState(false);
  const preRefreshPendingRef = useRef<string | null>(null);

  const deepLinkStateRef = useRef<{
    targetId: string | null;
    pagesLoaded: number;
    startedAt: number;
    done: boolean;
    inFlight: boolean;
  }>({ targetId: null, pagesLoaded: 0, startedAt: 0, done: false, inFlight: false });

  useEffect(() => {
    return () => {
      consumedDeepLinkIdRef.current = null;
      refreshedForDeepLinkRef.current = null;
      preRefreshPendingRef.current = null;
    };
  }, []);

  // Sync refs when targetCommentId changes
  useEffect(() => {
    if (targetCommentId) {
      // If we have a new target, we might need to reset some tracking if it's different from the last one
      // But usually we just let the pre-refresh effect handle it based on targetCommentId
    }
  }, [targetCommentId]);

  // Deep linking failure reasons
  enum DeepLinkFailureReason {
    TIMEOUT = 'took longer than 3 seconds',
    TOO_DEEP = 'target is more than 5 pages deep',
    NOT_FOUND = 'no more pages',
  }
  const openReactionModal = useCallback((commentId: string) => {
    setReactionModalCommentId(commentId);
    setReactionModalVisible(true);
  }, []);
  const closeReactionModal = useCallback(() => {
    setReactionModalVisible(false);
    setReactionModalCommentId(null);
  }, []);

  // Pre-refresh: always refresh once before deep-link searching to ensure we have latest data
  useEffect(() => {
    if (!scrollToComment || !targetCommentId || loading || skipInitialDeepLinkRefresh) return;

    // Skip if already consumed or already refreshing for this target
    if (consumedDeepLinkIdRef.current === targetCommentId || 
        refreshedForDeepLinkRef.current === targetCommentId) {
      return;
    }

    logger.info(`[GroupDiscussion] Pre-refreshing before deep link search: ${targetCommentId}`);
    refreshedForDeepLinkRef.current = targetCommentId;
    preRefreshPendingRef.current = targetCommentId;
    setIsWaitingForPreRefresh(true);
    handleRefresh();
  }, [scrollToComment, targetCommentId, loading, handleRefresh, skipInitialDeepLinkRefresh]);

  // Clear wait state once refresh finishes
  useEffect(() => {
    if (!isWaitingForPreRefresh) return;
    if (refreshing) return;
    logger.info('[GroupDiscussion] Pre-refresh finished, ready to deep link');
    preRefreshPendingRef.current = null;
    setIsWaitingForPreRefresh(false);
    // Reset the timer so the 3s timeout only counts from after pre-refresh,
    // not from initial mount (which may predate the CoC/ToS flow completion).
    const state = deepLinkStateRef.current;
    if (state.targetId) {
      state.startedAt = Date.now();
      state.done = false;
      state.inFlight = false;
    }
  }, [isWaitingForPreRefresh, refreshing]);

  // Deep linking effect
  useEffect(() => {
    if (!scrollToComment || !targetCommentId || !comments || activeTargetCommentId === targetCommentId) {
      return;
    }

    // Wait for pre-refresh to complete
    if (isWaitingForPreRefresh || preRefreshPendingRef.current === targetCommentId) {
      return;
    }

    const state = deepLinkStateRef.current;
    if (state.targetId !== targetCommentId) {
      state.targetId = targetCommentId;
      state.pagesLoaded = 0;
      state.startedAt = Date.now();
      state.done = false;
      state.inFlight = false;
      setIsDeepLinkLoading(true);
      onDeepLinkLoadingChange?.(true);
    }

    if (state.done || state.inFlight) return;

    const performDeepLink = async () => {
      state.inFlight = true;

      try {
        const maxPages = 5;
        // Search in both regular and pinned comments
        const foundResult = findCommentWithParent(comments, targetCommentId) || 
                           findCommentWithParent(pinnedComments, targetCommentId);

        if (foundResult) {
          const isReplyTarget = Boolean(foundResult.parent);
          const scrollTargetId = foundResult.parent?.uuid ?? foundResult.comment.uuid;

          setActiveTargetCommentId(targetCommentId);
          setShouldPersistHighlight(true);

          setTimeout(() => {
            setShouldPersistHighlight(false);
            setHighlightCommentId(null);
            setActiveTargetReplyId(null);
          }, 3000);

          setExpandedThreadCommentId(scrollTargetId);

          if (isReplyTarget) {
            setHighlightCommentId(null);
            setActiveTargetReplyId(targetCommentId);
          } else {
            setHighlightCommentId(targetCommentId);
            setActiveTargetReplyId(null);
          }

          setPendingScrollTargetId(scrollTargetId);
          consumedDeepLinkIdRef.current = targetCommentId;

          state.done = true;
          logger.info(`[GroupDiscussion] Deep linked to comment ${targetCommentId}`);
          return;
        }

        const elapsedMs = Date.now() - state.startedAt;
        if (elapsedMs >= 3000) {
          // Don't timeout if the pre-refresh hasn't run yet — we may not have
          // fresh data. This can happen when initial loading took > 3 seconds
          // or when the component mounted early (e.g. during CoC/ToS flow).
          if (refreshedForDeepLinkRef.current !== targetCommentId) {
            return;
          }
          state.done = true;
          logger.error('[GroupDiscussion] Deep link timeout - comment took too long to load');
          showErrorMessage(
            'Comment not found',
            'The comment took too long to load. It may have been deleted or moved.'
          );
          return;
        }

        // If we're still loading the current page, wait for it to finish before loading more or giving up
        if (loading || refreshing) {
          return;
        }

        if (!hasMoreComments || state.pagesLoaded >= maxPages || !handleLoadMore) {
          state.done = true;
          const reason = state.pagesLoaded >= maxPages
            ? DeepLinkFailureReason.TOO_DEEP
            : DeepLinkFailureReason.NOT_FOUND;
          logger.error(`[GroupDiscussion] Deep link failed: ${reason}`);
          if (reason === DeepLinkFailureReason.TOO_DEEP) {
            logger.warn('[GroupDiscussion] Comment too old to load within limit');
          } else {
            logger.error('[GroupDiscussion] Comment not found - may be deleted');
          }

          if (reason === DeepLinkFailureReason.TOO_DEEP) {
            showWarningMessage(
              'Comment too old',
              'This comment is very old and couldn\'t be loaded within the limit. Try scrolling manually to find it.'
            );
          } else if (reason === DeepLinkFailureReason.NOT_FOUND) {
            showErrorMessage(
              'Comment not found',
              'The comment may have been deleted or is no longer available.'
            );
          }
          return;
        }

        await handleLoadMore();
        state.pagesLoaded += 1;
      } catch (error) {
        state.done = true;
        logger.error('[GroupDiscussion] Deep link error:', error);
      } finally {
        state.inFlight = false;
        if (state.done) {
          setIsDeepLinkLoading(false);
          onDeepLinkLoadingChange?.(false);
        }
      }
    };

    performDeepLink();
  }, [
    scrollToComment,
    targetCommentId,
    comments,
    activeTargetCommentId,
    hasMoreComments,
    handleLoadMore,
    onDeepLinkLoadingChange,
    loading,
    refreshing,
    isWaitingForPreRefresh,
  ]);

  // Helper function to find comment by ID (including nested replies) and track parent
  const findCommentWithParent = useCallback(
    (
      commentList: GroupComment[],
      targetId: string,
      parent?: GroupComment
    ): { comment: GroupComment; parent?: GroupComment } | undefined => {
      for (const comment of commentList) {
        if (comment.uuid === targetId) {
          return { comment, parent };
        }

        const replies = comment.children ?? comment.replies ?? [];
        if (replies.length > 0) {
          const foundInReplies = findCommentWithParent(replies, targetId, comment);
          if (foundInReplies) {
            return foundInReplies;
          }
        }
      }
      return undefined;
    },
    []
  );

  // Memoized flat comments list for FlashList
  const flatComments = useMemo((): Array<{ type: 'pinned' | 'comment'; data: GroupComment; id: string }> => {
    const result: Array<{ type: 'pinned' | 'comment'; data: GroupComment; id: string }> = [];

    // Add pinned comments first
    if (pinnedComments.length > 0) {
      pinnedComments.forEach((comment: GroupComment) => {
        result.push({
          type: 'pinned',
          data: comment,
          id: `pinned-${comment.uuid}`,
        });
      });
    }

    // Add regular comments
    comments.forEach((comment: GroupComment) => {
      result.push({
        type: 'comment',
        data: comment,
        id: comment.uuid,
      });
    });

    return result;
  }, [comments, pinnedComments]);

  // Retry scroll when list mounts or data updates (e.g., from notification deep links)
  useEffect(() => {
    if (!pendingScrollTargetId) return;
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 80;
    const intervalMs = 100;

    const tryScroll = () => {
      if (cancelled) return;

      const commentIndex = flatComments.findIndex(item => item.data.uuid === pendingScrollTargetId);
      if (commentIndex >= 0 && virtuosoRef.current) {
        try {
          virtuosoRef.current.scrollToIndex({
            index: commentIndex,
            align: 'center',
            behavior: 'smooth'
          });
        } catch (error) {
          logger.warn('[GroupDiscussion] scrollToIndex failed', error);
        }
      }

      if (typeof document !== 'undefined') {
        const el = document.querySelector(
          `[data-comment-id="${pendingScrollTargetId}"]`
        ) as HTMLElement | null;
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
          setPendingScrollTargetId(null);
          return;
        }
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        setTimeout(tryScroll, intervalMs);
      } else {
        logger.warn('[GroupDiscussion] Scroll retry exhausted for deep link target');
        setPendingScrollTargetId(null);
      }
    };

    tryScroll();
    return () => {
      cancelled = true;
    };
  }, [pendingScrollTargetId, flatComments]);

  // Get comment count for display
  const commentCount = useMemo(() => {
    const totalComments = comments.reduce((count: number, comment: GroupComment) => {
      return count + 1 + (comment.children?.length || 0);
    }, 0);
    return totalComments;
  }, [comments]);


  // Render individual comment item
  const renderCommentItem = useCallback(({ item }: { item: { type: 'pinned' | 'comment'; data: GroupComment; id: string } }) => {
    const { type, data: comment } = item;
    return (
      <CommentItem
        comment={comment}
        isPinned={type === 'pinned'}
        currentUserId={user?.uuid}
        groupMembers={groupMembers}
        replyLoading={replyLoading === comment.uuid}
        isHighlighted={shouldPersistHighlight && comment.uuid === highlightCommentId}
        highlightReplyId={
          shouldPersistHighlight && activeTargetReplyId && comment.children?.some(reply => reply.uuid === activeTargetReplyId)
            ? activeTargetReplyId
            : undefined
        }
        forceExpandReplies={Boolean(expandedThreadCommentId && comment.uuid === expandedThreadCommentId)}
        allowReactions={allowReactions && canReact}
        allowReplies={allowReplies && canPost}
        allowEditing={allowEditing}
        allowReporting={allowReporting && canReport}
        onReply={onComposeComment}
        onReact={handleReactToComment}
        onEdit={(comment) => logger.log('Edit comment:', comment)}
        onDelete={handleDeleteComment}
        onReport={handleReportUser}
        onUserProfile={onUserProfile}
        onHidePinned={hookData.hidePinnedComment}
        onShowReactionModal={openReactionModal}
        onFlagComment={handleFlagComment}
      />
    );
  }, [
    groupMembers,
    hookData.hidePinnedComment,
    replyLoading,
    allowReactions,
    allowReplies,
    allowEditing,
    allowReporting,
    canReact,
    canReport,
    canPost,
    onComposeComment,
    handleReactToComment,
    handleDeleteComment,
    handleFlagComment,
    handleReportUser,
    onUserProfile,
    openReactionModal,
    shouldPersistHighlight,
    highlightCommentId,
    activeTargetReplyId,
    expandedThreadCommentId,
  ]);

  // Render header section
  const renderHeader = useCallback(() => {
    if (!header && commentCount === 0) return null;

    return (
      <CobuStack gap="$3" pb="$3">
        {header}

        {/* Comment count */}
        {commentCount > 0 && !publicChat && (
          <CobuStack layout="horizontal" jc="space-between" ai="center" px="$4">
            <CobuText
              variant="body"
            >
              {commentCount === 1
                ? strings.comments.singular.replace('{count}', commentCount.toString())
                : strings.comments.plural.replace('{count}', commentCount.toString())
              }
            </CobuText>

            {/* Add comment button for groups */}
            {isGroup && onComposeComment && canPost && (
              <Button
                onPress={() => onComposeComment()}
                p="$2"
                h="auto"
                bc="$buttonTertiaryBackground"
                pressStyle={{ background: '$buttonTertiaryBackground' }}
                hoverStyle={{ background: '$buttonTertiaryBackground' }}
                focusStyle={{ background: '$buttonTertiaryBackground' }}
                br='$6'
              >
                <CobuText variant="body" color="$buttonTertiaryText" >
                  {createPostButtonText}
                </CobuText>
              </Button>
            )}
          </CobuStack>
        )}
      </CobuStack>
    );
  }, [header, commentCount, publicChat, isGroup, onComposeComment, strings, canPost]);

  // Render footer with load more button, plus a persistent bottom spacer
  const renderFooter = useCallback(() => {
    let footerContent = null;

    if (hasMoreComments || isLoadingMore) {
      if (showLoadMoreBtnToLoadMore) {
        footerContent = (
          <CobuStack ai="center" p="$4">
            {isLoadingMore ? (
              <CobuText variant="body" color="$colorSecondary">{strings.actions.loading}</CobuText>
            ) : (
              <CobuButton
                dsVariant="secondary"
                title={isGroup ? strings.buttons.loadMore : strings.buttons.loadOlder}
                onPress={handleLoadMore}
                w="60%"
              />
            )}
          </CobuStack>
        );
      } else if (isLoadingMore) {
        footerContent = (
          <CobuStack ai="center" p="$4">
            <CobuText variant="body" color="$colorSecondary">{strings.actions.loading}</CobuText>
          </CobuStack>
        );
      }
    }

    return (
      <>
        {footerContent}
      </>
    );
  }, [
    hasMoreComments,
    isLoadingMore,
    showLoadMoreBtnToLoadMore,
    isGroup,
    strings,
    theme,
    handleLoadMore,
  ]);

  // Render loading shimmer
  const renderLoadingShimmer = useCallback(() => (
    <CobuStack gap="$3" p="$4">
      {Array.from({ length: 3 }).map((_, index) => (
        <CommentShimmer key={index} />
      ))}
    </CobuStack>
  ), []);

  // Render empty state
  const renderEmptyState = useCallback(() => {
    if (emptyState) return emptyState;

    logger.log('emptyState', emptyState);
    logger.log('onComposeComment', onComposeComment);
    
    const buttonText = isMarketplace
      ? Strings.get().discussion.marketplace.composeButton
      : strings.emptyState.createFirstPost;

    return (
      <EmptyDiscussionState
        title={strings.emptyState.title}
        subtitle={strings.emptyState.subtitle}
        buttonText={buttonText}
        onButtonPress={onComposeComment}
        showButton={!!onComposeComment && canPost}
      />
    );
  }, [emptyState, strings, onComposeComment, isMarketplace, canPost]);

  // Render floating add comment button for public chat
  const renderFloatingButton = useCallback(() => {
    if (!publicChat || !onComposeComment || !canPost) return null;

    return (
      <FloatingCommentButton
        onPress={() => onComposeComment()}
        text={composeButtonText}
        // iconName="add-comment" // Not supported in web FloatingCommentButton props
        disabled={loading}
      />
    );
  }, [publicChat, onComposeComment, composeButtonText, loading, canPost]);

  // react-virtuoso item renderer helpers
  type FlatItem = { type: 'pinned' | 'comment'; data: GroupComment; id: string };
  const itemContent = (index: number) => {
    const item = flatComments[index] as FlatItem;
    return (
      <CobuStack px={0} data-comment-id={item.data.uuid} id={`comment-${item.data.uuid}`}>
        {renderCommentItem({ item } as any)}
      </CobuStack>
    );
  };
  const computeItemKey = (index: number) => `${flatComments[index]?.id ?? 'item'}-${index}`;

  // Main render
  if (loading && comments.length === 0) {
    return (
      <CobuStack bg="$background">
        {renderHeader()}
        {renderLoadingShimmer()}
      </CobuStack>
    );
  }

  return (
    <EmojiProvider>
    <CobuStack bg="$white">
      <CobuStack>
        {renderHeader()}
        {flatComments.length === 0 ? (
          renderEmptyState()
        ) : (
          <>
            {loading ? (
              renderLoadingShimmer()
            ) : virtualize ? (
              <Virtuoso
                ref={virtuosoRef}
                data={flatComments}
                computeItemKey={computeItemKey}
                itemContent={(_, item) => itemContent(_) }
                components={{
                  Footer: () => (
                    <>{renderFooter()}</>
                  ),
                }}
                endReached={!showLoadMoreBtnToLoadMore ? handleLoadMore : undefined}
                overscan={400}
                useWindowScroll
                style={{ width: '100%' }}
              />
            ) : (
              <CobuStack gap="$2">
                {flatComments.map((fc, idx) => (
                  <CobuStack
                    key={computeItemKey(idx)}
                    px={0}
                    data-comment-id={fc.data.uuid}
                    id={`comment-${fc.data.uuid}`}
                  >
                    {renderCommentItem({ item: fc } as any)}
                  </CobuStack>
                ))}
                {renderFooter()}
              </CobuStack>
            )}
          </>
        )}
      </CobuStack>

      {renderFloatingButton()}

      {/* Reactions Modal - not implemented */}
      <ReactionsModal
        visible={isReactionModalVisible}
        onClose={closeReactionModal}
        commentId={reactionModalCommentId}
        commentType={commentType}
      />
    </CobuStack>
    </EmojiProvider>
  );
});

GroupDiscussion.displayName = 'GroupDiscussion';

export default GroupDiscussion; 
